// Library imports
import React, { useState, useEffect } from 'react';
// Component imports
import NoData from '../../../../components/NoData';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import CompareList from '../../../../components/CompareList';
import TabNav from '../../../../components/TabNav';
import PitcherHistoryModal from './PitcherHistoryModal';
import PlayerData from '../Rosters/PlayerData';
// API imports
import { getPitcherVsOpponentGames } from '../../../../api/games';
// Util imports
import { insert } from '../../../../utils';
import { formatInitialLastName } from '../../../../utils/strings';
import { keyFactorData } from '../KeyFactors/utils';
import { formatShortDate } from '../../utils';
// Styled components
import {
    InnerContent,
    VsOpponentContainer,
    HistoricalGamesContainer,
    PitcherSection,
    PitcherTitle,
    PitcherLink,
    StarterHeaders,
    GamesInfo,
    GamesListContainer,
    GameCard,
    GameDateResult,
    GameScore,
    OpposingPitcher,
    NoGamesFound,
} from './styles';

const Pitchers = ({ sport, game }) => {
    const [tab, setTab] = useState(game.awayTeam.pitcher && game.homeTeam.pitcher ? 'Starters' : 'team');
    const [dataFields, setDataFields] = useState([]);
    const [historicalGames, setHistoricalGames] = useState({ away: [], home: [] });
    const [selectedGame, setSelectedGame] = useState(null);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState('pitchers');
    const [selectedPlayer, setSelectedPlayer] = useState(null);

    const tabs = game.awayTeam.pitcher && game.homeTeam.pitcher ? ['Starters', 'opponent', 'team'] : ['team'];

    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true);

                if (tab === 'opponent') {
                    // Fetch historical games for both pitchers vs opposing teams
                    const [awayPitcherGames, homePitcherGames] = await Promise.all([
                        getPitcherVsOpponentGames(sport, game.awayTeam.pitcher.firstName, game.awayTeam.pitcher.lastName, game.homeTeam.teamName, 5),
                        getPitcherVsOpponentGames(sport, game.homeTeam.pitcher.firstName, game.homeTeam.pitcher.lastName, game.awayTeam.teamName, 5),
                    ]);

                    setHistoricalGames({ away: awayPitcherGames, home: homePitcherGames });

                    // Calculate historical stats
                    const awayStats = calculatePitcherStats(awayPitcherGames, game.awayTeam.pitcher);
                    const homeStats = calculatePitcherStats(homePitcherGames, game.homeTeam.pitcher);

                    setDataFields({
                        labels: ['Games', 'Team Wins'],
                        awayFactors: awayStats,
                        homeFactors: homeStats,
                    });
                } else {
                    const data = await keyFactorData(sport, game.awayTeam, game.homeTeam, tab === 'Starters' ? 'pitcher' : 'pitching');
                    if (tab === 'Starters') {
                        const awayPitcherEra = game.awayTeam.pitcher?.splits?.away?.era ? game.awayTeam.pitcher.splits.away.era.toFixed(2) : 0.0;
                        const homePitcherEra = game.homeTeam.pitcher?.splits?.home?.era ? game.homeTeam.pitcher.splits.home.era.toFixed(2) : 0.0;
                        const awayPitcherWalks = game.awayTeam.pitcher?.splits?.away?.walks ? game.awayTeam.pitcher.splits.away.walks : 0.0;
                        const homePitcherWalks = game.homeTeam.pitcher?.splits?.home?.walks ? game.homeTeam.pitcher.splits.home.walks : 0.0;
                        data.awayFactors = insert(data.awayFactors, 1, {
                            label: 'A/H ERA',
                            value: awayPitcherEra,
                            class: awayPitcherEra === homePitcherEra ? 'light' : awayPitcherEra < homePitcherEra ? 'success' : 'danger',
                        });
                        data.awayFactors = insert(data.awayFactors, 2, {
                            label: 'A/H BB',
                            value: awayPitcherWalks,
                            class: awayPitcherWalks === homePitcherWalks ? 'light' : awayPitcherWalks < homePitcherWalks ? 'success' : 'danger',
                        });
                        data.homeFactors = insert(data.homeFactors, 1, {
                            label: 'A/H ERA',
                            value: homePitcherEra,
                            class: awayPitcherEra === homePitcherEra ? 'light' : homePitcherEra < awayPitcherEra ? 'success' : 'danger',
                        });
                        data.homeFactors = insert(data.homeFactors, 2, {
                            label: 'A/H BB',
                            value: homePitcherWalks,
                            class: awayPitcherWalks === homePitcherWalks ? 'light' : homePitcherWalks < awayPitcherWalks ? 'success' : 'danger',
                        });
                        data.labels = insert(data.labels, 1, 'A/H ERA');
                        data.labels = insert(data.labels, 2, 'A/H BB');
                    }
                    setDataFields(data);
                }
            } catch (error) {
                console.log(`Error getting ${tab} data for: ${sport}. ${error.message}`);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [tab, sport, game]);

    // Calculate stats from historical games
    const calculatePitcherStats = (games, pitcher) => {
        if (!games || games.length === 0) {
            return [
                { label: 'Games', value: 0, class: 'light' },
                { label: 'Wins', value: 0, class: 'light' },
            ];
        }
        let totalWins = 0;
        games.forEach(game => {
            // Determine if pitcher was home or away
            const isHome =
                game.homeTeam.pitcher && game.homeTeam.pitcher.firstName === pitcher.firstName && game.homeTeam.pitcher.lastName === pitcher.lastName;
            const pitcherTeam = isHome ? game.homeTeam : game.awayTeam;
            if (pitcherTeam.pitcher?.stats && game.winner === pitcherTeam.teamId) {
                totalWins++;
            }
        });
        const winPercentage = games.length > 0 ? totalWins / games.length : 0;
        return [
            { label: 'Games', value: games.length, class: games.length >= 3 ? 'success' : 'light' },
            { label: 'Team Wins', value: `${totalWins}-${games.length - totalWins}`, class: winPercentage > 0.5 ? 'success' : 'danger' },
        ];
    };

    const handlePitcherClick = pitcher => {
        if (!pitcher?.playerId) return;
        setSelectedPlayer({
            id: pitcher.playerId,
            name: `${pitcher.firstName} ${pitcher.lastName}`,
            position: 'SP',
        });
        setView('player-data');
    };

    // Handle game click to show details
    const handleGameClick = gameData => {
        setSelectedGame(gameData);
    };

    // Close game details modal
    const closeGameDetails = () => {
        setSelectedGame(null);
    };

    // Render individual historical game
    const renderHistoricalGame = (gameData, pitcher) => {
        const isHome =
            gameData.homeTeam.pitcher && gameData.homeTeam.pitcher.firstName === pitcher.firstName && gameData.homeTeam.pitcher.lastName === pitcher.lastName;

        const pitcherTeam = isHome ? gameData.homeTeam : gameData.awayTeam;
        const opponentTeam = isHome ? gameData.awayTeam : gameData.homeTeam;
        const opposingPitcher = isHome ? gameData.awayTeam.pitcher : gameData.homeTeam.pitcher;
        const pitcherWon = gameData.winner === pitcherTeam.teamId;

        return (
            <GameCard
                key={gameData._id || `${gameData.awayTeam.teamId}-${gameData.homeTeam.teamId}-${gameData.dateTime}`}
                onClick={() => handleGameClick(gameData)}
                won={pitcherWon}
            >
                <GameDateResult>
                    {formatShortDate(gameData.dateTime)} - {pitcherWon ? 'W' : 'L'}
                </GameDateResult>
                <GameScore>
                    vs {opponentTeam.teamName} ({pitcherTeam.score ?? 'N/A'} - {opponentTeam.score ?? 'N/A'})
                </GameScore>
                {opposingPitcher && (
                    <OpposingPitcher>
                        vs {opposingPitcher.firstName} {opposingPitcher.lastName}
                    </OpposingPitcher>
                )}
            </GameCard>
        );
    };

    if (!game.awayTeam.pitcher || !game.homeTeam.pitcher) {
        return <NoData title="No data" message="There is currently no pitching data for this game.  Check back soon." />;
    }

    if (view === 'player-data' && selectedPlayer) {
        return <PlayerData player={selectedPlayer} setView={() => setView('pitchers')} setSelectedProp={() => {}} setPlayerWithProps={() => {}} />;
    }

    const getTabTitle = tab => {
        if (tab === 'Starters') {
            return {
                away: `${game.awayTeam.pitcher.firstName} ${game.awayTeam.pitcher.lastName}`,
                home: `${game.homeTeam.pitcher.firstName} ${game.homeTeam.pitcher.lastName}`,
            };
        } else if (tab === 'opponent') {
            return {
                away: `${formatInitialLastName(game.awayTeam.pitcher.firstName, game.awayTeam.pitcher.lastName)} vs ${game.homeTeam.abbr}`,
                home: `${formatInitialLastName(game.homeTeam.pitcher.firstName, game.homeTeam.pitcher.lastName)} vs ${game.awayTeam.abbr}`,
            };
        } else {
            return {
                away: game.awayTeam.teamName,
                home: game.homeTeam.teamName,
            };
        }
    };

    const titles = getTabTitle(tab);

    return (
        <InnerContent fluid>
            <TabNav tabs={tabs} activeTab={tab} setTab={setTab} containerStyle={{ marginBottom: '1rem' }} />
            {loading ? (
                <LoadingSpinner />
            ) : (
                <>
                    {tab === 'opponent' ? (
                        <VsOpponentContainer>
                            {/* Summary Stats */}
                            <CompareList
                                labels={dataFields.labels}
                                dataSetOneTitle={titles.away}
                                dataSetOneTitleColor={game.awayTeam.color}
                                dataSetOneLogo={game.awayTeam.logoUrl}
                                dataSetOne={dataFields.awayFactors}
                                dataSetTwoTitle={titles.home}
                                dataSetTwoTitleColor={game.homeTeam.color}
                                dataSetTwoLogo={game.homeTeam.logoUrl}
                                dataSetTwo={dataFields.homeFactors}
                            />

                            {/* Historical Games */}
                            <HistoricalGamesContainer>
                                {/* Away Pitcher Historical Games */}
                                <PitcherSection>
                                    <PitcherTitle>
                                        <PitcherLink
                                            clickable={!!game.awayTeam.pitcher.playerId}
                                            onClick={() => handlePitcherClick(game.awayTeam.pitcher)}
                                        >
                                            {formatInitialLastName(game.awayTeam.pitcher.firstName, game.awayTeam.pitcher.lastName)} vs {game.homeTeam.abbr}
                                            {game.awayTeam.pitcher.playerId && ' →'}
                                        </PitcherLink>
                                    </PitcherTitle>
                                    <GamesInfo>{historicalGames.away.length} previous game(s) - Click to view details</GamesInfo>
                                    <GamesListContainer>
                                        {historicalGames.away.length > 0 ? (
                                            historicalGames.away.map(gameData => renderHistoricalGame(gameData, game.awayTeam.pitcher))
                                        ) : (
                                            <NoGamesFound>No historical games found</NoGamesFound>
                                        )}
                                    </GamesListContainer>
                                </PitcherSection>

                                {/* Home Pitcher Historical Games */}
                                <PitcherSection>
                                    <PitcherTitle>
                                        <PitcherLink
                                            clickable={!!game.homeTeam.pitcher.playerId}
                                            onClick={() => handlePitcherClick(game.homeTeam.pitcher)}
                                        >
                                            {formatInitialLastName(game.homeTeam.pitcher.firstName, game.homeTeam.pitcher.lastName)} vs {game.awayTeam.abbr}
                                            {game.homeTeam.pitcher.playerId && ' →'}
                                        </PitcherLink>
                                    </PitcherTitle>
                                    <GamesInfo>{historicalGames.home.length} previous game(s) - Click to view details</GamesInfo>
                                    <GamesListContainer>
                                        {historicalGames.home.length > 0 ? (
                                            historicalGames.home.map(gameData => renderHistoricalGame(gameData, game.homeTeam.pitcher))
                                        ) : (
                                            <NoGamesFound>No historical games found</NoGamesFound>
                                        )}
                                    </GamesListContainer>
                                </PitcherSection>
                            </HistoricalGamesContainer>
                        </VsOpponentContainer>
                    ) : (
                        <>
                            {tab === 'Starters' && (game.awayTeam.pitcher.playerId || game.homeTeam.pitcher.playerId) && (
                                <StarterHeaders>
                                    <div style={{ flex: 1, textAlign: 'left' }}>
                                        {game.awayTeam.pitcher.playerId && (
                                            <PitcherLink color="#93c5fd" clickable onClick={() => handlePitcherClick(game.awayTeam.pitcher)}>
                                                View Stats →
                                            </PitcherLink>
                                        )}
                                    </div>
                                    <div style={{ flex: 1, textAlign: 'right' }}>
                                        {game.homeTeam.pitcher.playerId && (
                                            <PitcherLink color="#93c5fd" clickable onClick={() => handlePitcherClick(game.homeTeam.pitcher)}>
                                                View Stats →
                                            </PitcherLink>
                                        )}
                                    </div>
                                </StarterHeaders>
                            )}
                            <CompareList
                                labels={dataFields.labels}
                                dataSetOneTitle={titles.away}
                                dataSetOneTitleColor={game.awayTeam.color}
                                dataSetOneLogo={game.awayTeam.logoUrl}
                                dataSetOne={dataFields.awayFactors}
                                dataSetTwoTitle={titles.home}
                                dataSetTwoTitleColor={game.homeTeam.color}
                                dataSetTwoLogo={game.homeTeam.logoUrl}
                                dataSetTwo={dataFields.homeFactors}
                            />
                        </>
                    )}

                    {/* Game Details Modal */}
                    <PitcherHistoryModal isOpen={!!selectedGame} onClose={closeGameDetails} gameData={selectedGame} loading={false} />
                </>
            )}
        </InnerContent>
    );
};

export default Pitchers;
